import React, { useState, useEffect } from "react";
import { Input, Card, Row, Col, Button, Typography, Avatar } from "antd";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { FormOutlined, ArrowLeftOutlined, UserOutlined, EnvironmentOutlined, LinkOutlined } from '@ant-design/icons';
import ExternalForms from "./externalForms";
import CaDISupplyChain from "./cadiSupplyChain";
import { FaWpforms } from "react-icons/fa";

const { Title } = Typography;

// Unified app configuration with components, metadata and styling
const appsConfig = [
  {
    id: "forms",
    title: "External Forms",
    description: "Create your own external forms to capture data from your customers",
    icon: <FormOutlined style={{ fontSize: 24 }} />,
    component: ExternalForms,
    color: "#1890ff", // Blue
  },
  {
    id: "internal-forms",
    title: "Internal Forms",
    description: "Create forms to collect data from your own employees",
    icon: <UserOutlined style={{ fontSize: 24 }} />,
    component: ExternalForms, // Using same component for now
    color: "#52c41a", // Green
  },
  {
    id: "carbon-footprint-surveys",
    title: "Carbon Footprint Surveys",
    description: "Pre-made surveys for carbon footprint assessment",
    icon: <FaWpforms style={{ fontSize: 24 }} />,
    component: ExternalForms, // Using same component for now
    color: "#722ed1", // Purple
  },
  {
    id: "cadi-supply-chain",
    title: "CaDI Supply Chain",
    description: "Request calculated emissions data from your supply chain",
    icon: <img src="https://www.carbondi.com/cadi.png" alt="CaDI" style={{ width: 24, height: 24 }} />,
    component: CaDISupplyChain,
    color: "#3c9ee1", // Light blue
    customImage: true,
  },
  // Add more app objects here with unique IDs
];

// Create component mapping from config
const appComponents = appsConfig.reduce((acc, app) => {
  acc[app.id] = app.component;
  return acc;
}, {});

export default function Apps() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeApp, setActiveApp] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Check URL params on component mount
  useEffect(() => {
    const appParam = searchParams.get("app");
    if (appParam) {
      const app = appsConfig.find(a => a.id === appParam);
      if (app) {
        setActiveApp(app);
      }
    }
  }, [searchParams]);

  const handleAppClick = (app) => {
    setActiveApp(app);
    setSearchParams({ app: app.id });
  };

  const handleBack = () => {
    setActiveApp(null);
    setSearchParams({});
  };

  const filteredApps = appsConfig.filter(app =>
    app.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Render active app component if selected
  if (activeApp) {
    const AppComponent = appComponents[activeApp.id];
    return (
      <div>
        <Title level={2} style={{ marginBottom: 24 }}>{activeApp.title}</Title>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={handleBack}
          style={{ marginBottom: 20 }}
        >
          Back to Apps
        </Button>
        {AppComponent ? <AppComponent /> : <p>App component not found</p>}
      </div>
    );
  }

  // Render apps list
  return (
    <div>
      <Title level={2} style={{ marginBottom: 24 }}>Sustrax Apps</Title>
      <Input.Search
        placeholder="Search apps"
        onChange={e => setSearchTerm(e.target.value)}
        style={{ marginBottom: 24 }}
        size="large"
      />
      <Row gutter={[16, 16]}>
        {filteredApps.map(app => (
          <Col xs={24} sm={12} md={8} lg={6} key={app.id}>
            <Card 
              hoverable
              onClick={() => handleAppClick(app)}
              style={{ 
                height: '100%', 
                transition: 'all 0.3s',
                boxShadow: '0 2px 8px rgba(0,0,0,0.09)'
              }}
              className="app-card"
            >
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                {app.customImage ? (
                  <Avatar 
                    size={64} 
                    src="https://www.carbondi.com/cadi.png"
                    style={{ background: app.color }}
                  />
                ) : (
                  <div style={{ 
                    background: app.color, 
                    borderRadius: '50%', 
                    width: 64, 
                    height: 64, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    margin: '0 auto',
                    color: '#ffffff'
                  }}>
                    {app.icon}
                  </div>
                )}
              </div>
              <Card.Meta
                title={<span style={{ fontSize: '16px', fontWeight: 'bold' }}>{app.title}</span>}
                description={<span style={{ fontSize: '14px' }}>{app.description}</span>}
                style={{ textAlign: 'center' }}
              />
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}


